import { ITaxonomyField } from '@jumpstart/core';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import { collection, doc, getDocs, getFirestore, query, QuerySnapshot, setDoc, where } from 'firebase/firestore';
import React, { useState } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import usePlacesAutocomplete, { getDetails, Suggestion } from 'use-places-autocomplete';
import { app, db } from '../../../core/firebase';
import { formStyles } from '../../event-creation/styles';
import { buildOrganization, IOrganizationEnhanced } from '../utils';

export interface IBusinessSearch {
  position: any | null;
  taxonomy: ITaxonomyField;
  error: boolean;
  helperText: string;
  onCreated(organization: IOrganizationEnhanced): void;
  onChange(value: string): void;
}

export default function BusinessSearch(props: IBusinessSearch) {
  const { position, taxonomy, error, helperText, onChange, onCreated } = props;
  const [focus, setFocus] = useState(false);
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions
  } = usePlacesAutocomplete({
    requestOptions: {
      types: ['establishment']
      // location: new google.maps.LatLng({lat: props.position.coords.latitude, lng: props.position.coords.longitude})
    },
    debounce: 300
  });

  const ref = useOnclickOutside(() => {
    // When user clicks outside of the component, we can dismiss
    // the searched suggestions by calling this method
    clearSuggestions();
  });

  const handleInput = (e: any) => {
    // Update the keyword of the input element
    setValue(e.target.value);
    onChange(e.target.value);
  };

  const handleDetails = (placeId: string): Promise<google.maps.places.PlaceResult> => {
    const parameter = {
      // Use the "place_id" of suggestion from the dropdown (object), here just taking first suggestion for brevity
      placeId
      // Specify the return data that you want (optional)
      // fields: ['name', 'rating', 'types']
    };
    return getDetails(parameter)
      .then((details: google.maps.places.PlaceResult | string) => {
        if (typeof details === 'string') {
          console.error('What to do with this?', details);
          return {name: 'unknown'};
        } else {
          return details;
        }
      })
      .catch((error: Error) => {
        console.error('Error: ', error);
        return {name: 'unknown'};
      });
  };

  const handleOrganization = (suggestion: Suggestion) => {
    console.log('Checking if organization exists');

    const ref = collection(getFirestore(app), 'scout9-organizations')
    getDocs(query(ref, where("places.place_id", "==", "suggestion.place_id")))
      .then((query: QuerySnapshot) => {
        if (query.empty) {
          console.log('Organization does not exist');
          handleDetails(suggestion.place_id)
            .then((results: google.maps.places.PlaceResult) => {
              const organization: IOrganizationEnhanced = buildOrganization(taxonomy, suggestion, results);
              console.log('Building organization', organization);
              console.log('Building organization (raw)', JSON.parse(JSON.stringify(organization)));
              onChange(organization.id);
              setDoc(doc(db, 'scout9-organizations', organization.id), JSON.parse(JSON.stringify(organization)))
                .then(() => {
                  onCreated(organization);
                  console.log('Organization added');
                })
                .catch((err: Error) => {
                  console.error('Failed to add organization', err);
                });
            })
            .catch((err: Error) => {
              console.error('Failed to get details', err);
            });
        } else {
          console.log('Organization exists');
        }
      });
  };


  const handleSelect =
    // @ts-ignore
    (suggestion: Suggestion) =>
      () => {
        const { description } = suggestion;
        // When user selects a place, we can replace the keyword without request data from API
        // by setting the second parameter to "false"
        setValue(description, false);
        onChange(description);
        clearSuggestions();
        if (process.env.NODE_ENV === 'development') {
          handleOrganization(suggestion);
        }
      };

  const renderSuggestions = () =>
    data.map((suggestion: Suggestion) => {
      const {
        place_id,
        structured_formatting: { main_text, secondary_text }
      } = suggestion;

      return (
        <ListItem key={place_id} onClick={handleSelect(suggestion)} button>
            <ListItemText primary={main_text} secondary={secondary_text}/>
        </ListItem>
      );
    });

  return (
    <div ref={ref}>
      <TextField
        sx={formStyles.input}
        value={value}
        onChange={handleInput}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        disabled={!ready}
        label={`Preferred ${taxonomy.name} Business (optional)`}
        style={{ width: '100%' }}
        error={error}
        helperText={helperText}
        multiline
      />
      {/* We can use the "status" to decide whether we should display the dropdown or not */}

      <Collapse in={focus && data.length > 0}>
        <Paper>
          <List>{renderSuggestions()}</List>
        </Paper>
      </Collapse>
    </div>
  );
};
